/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import IconButton from '@mui/material/IconButton';
import Icon from "@mui/material/Icon";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MDButton from "components/MDButton";
import { Controller, useForm } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CustomAlert from "components/CustomAlert";
import Switch from "@mui/material/Switch";

import { useEffect, useState, useMemo } from "react";
import { UserAPI } from "api/UserAPI";
import { RoleAPI } from "api/RoleAPI";
import { useMaterialUIController, setLoading } from "context";
import TableSkeleton from "components/TableSkeleton";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import CircularProgress from '@mui/material/CircularProgress';
import permissions from "permissions";


function ListUser(props) {
  const [controller, dispatch] = useMaterialUIController();
  const [users, setUsers] = useState([]);
  const { user, loading, darkMode, admin } = controller;
  const [disabled, setDisabled] = useState(false);

  //Alert
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState('');
  const [alertColor, setAlertColor] = useState('info');

  //Modal
  const [open, setOpen] = useState(false);

  const { handleSubmit, setValue, control, formState: { isSubmitting }, } = useForm({ mode: "onChange" });

  const handleAlert = (content, color) => {
    setAlertContent(content);
    setAlertColor(color);
    setAlert(true)
  }

  const handleAlertClose = () => setAlert(false);

  const handleOpen = (id) => {
    setValue("id", id)
    setOpen(true);
  }

  const handleClose = (event, reason) => {
    if (reason && reason == "backdropClick")
      return;
    setOpen(false);
  }

  //roles
  const [roles, setRoles] = useState([]);

  const renderTable = useMemo(() => {
    let rows = [];

    const columns = [
      { Header: "usuario", accessor: "user", width: "45%", align: "left" },
      { Header: "rol", accessor: "role" },
      { Header: "acción", accessor: "action", align: "center" },
    ];

    users.map((element) => {
      rows = [...rows,
      {
        user: element.email,
        role: element.roles.length > 0 ? element.roles[0].name : '',
        action: (
          <MDBox>
            {(user?.permissions?.includes(permissions.users[4]) || user?.role === admin) && (
              <IconButton aria-label="add" value={element.id} onClick={() => handleOpen(element.id)}>
                <Icon fontSize="small">add</Icon>
              </IconButton>
            )}
            {(user?.permissions?.includes(permissions.users[3]) || user?.role === admin) && (
              <Switch aria-label="Color switch demo" color="warning" disabled={disabled} checked={element.active} onChange={() => handleEnable(element.id)}></Switch>

            )}
          </MDBox>
        ),
      }]
    });

    return <DataTable
      table={{ columns, rows }}
      isSorted={true}
      entriesPerPage={true}
      showTotalEntries={true}
      noEndBorder
      canSearch={true}
    />
  }, [users])

  const handleEnable = (id) => {
    setDisabled(true);
    enableUser(id)
  }

  const onSubmit = (data) => {
    return UserAPI.assignRole(data)
      .then(response => {
        const updatedData = users.map(item => {
          if (item.id === response.user.id) {
            return { ...item, roles: response.user.roles };
          }
          return item;
        });
        setUsers(updatedData);
        handleClose();
        handleAlert(response.message, 'success')
      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
        }
      });
  }

  const enableUser = (id) => {
    return UserAPI.enableUser(id)
      .then(response => {
        const updatedState = users.map(item => {
          if (item.id === response.user.id) {
            return { ...item, active: response.user.active };
          }
          return item;
        });
        setUsers(updatedState);
        handleAlert(response.message, 'success');
      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
        }
      }).finally(() => {
        setDisabled(false)
      });

  }

  const getUsersAndRoles = (signal) => {
    return Promise.all([UserAPI.index(signal), RoleAPI.index(signal)])
      .then(function ([users, roles]) {
        setUsers(users.users)
        setRoles(roles.roles)
      })
      .catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
        }
      }).finally(() => {
        setLoading(dispatch, false)
      });
  }

  useEffect(() => {
    window.history.replaceState({}, document.title)

    const abortController = new AbortController();
    const signal = abortController.signal
    setLoading(dispatch, true)
    getUsersAndRoles(signal)

    return () => {
      abortController.abort();
    };

  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} lg={12}>
            <MDBox mb={4} px={2}>
              <CustomAlert severity={alertColor} message={alertContent} open={alert} onClose={handleAlertClose}></CustomAlert>
            </MDBox>
            <Card>
              {loading ? <TableSkeleton /> :
                <MDBox pt={3}>
                  {renderTable}
                </MDBox>
              }
              <Dialog open={open} onClose={handleClose} >
                <MDBox style={{ backgroundColor: darkMode && themeDark.palette.dark.main }} component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
                  <DialogTitle>Asignar Rol</DialogTitle>
                  <DialogContent>
                    <Controller
                      name="role"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          value={(roles.find((role) => role.id === value)) || null}
                          options={roles}
                          getOptionLabel={(role) => role.name}
                          renderInput={(params) => (
                            <TextField {...params} label="Roles" variant="standard" />
                          )}
                          onChange={(event, item) => {
                            onChange(item?.id)
                          }}
                        />
                      )}
                    />
                  </DialogContent>
                  <DialogActions>
                    <MDButton onClick={handleClose}>Cancelar</MDButton>
                    <MDButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? <CircularProgress size={18} color="info" /> : 'Guardar'}
                    </MDButton>
                  </DialogActions>
                </MDBox>
              </Dialog>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ListUser;